/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, Table, TableBody,
  TableCell, TableContainer, TableRow, TextField, Switch, FormControlLabel,
  Divider, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import SearchableSelect from 'components/SearchableSelect';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { formKeyHandler } from 'utils/formKeyboard';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const TH = { py: 1.5, px: 2, textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, color: '#999', borderBottom: '1.5px solid #f0f2f5', whiteSpace: 'nowrap', letterSpacing: '0.06em' };
const td = (last) => ({ py: 1.25, px: 2, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.05)', fontSize: '0.8rem' });

const emptyForm = () => ({
  name: '',
  code: '',
  address: '',
  is_default: false,
  is_active: true,
  irm_pharm_pharmacies_id: '',
});

function LocationFormDialog({ open, onClose, onSaved, editing, pharmacies }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '',
        code: editing.code || '',
        address: editing.address || '',
        is_default: editing.is_default === 'Y',
        is_active: editing.is_active !== 'N',
        irm_pharm_pharmacies_id: String(editing.irm_pharm_pharmacies_id || ''),
      });
    } else {
      setForm(emptyForm());
    }
  }, [editing, open]);

  const up = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value.toUpperCase() }));

  const handleSave = async () => {
    if (!form.name.trim()) { Swal.fire({ icon: 'warning', title: 'Name is required' }); return; }
    if (!form.irm_pharm_pharmacies_id) { Swal.fire({ icon: 'warning', title: 'Select a pharmacy' }); return; }
    const payload = {
      name: form.name,
      code: form.code || `LOC-${Date.now()}`,
      address: form.address || null,
      is_default: form.is_default ? 'Y' : 'N',
      is_active: form.is_active ? 'Y' : 'N',
      irm_pharm_pharmacies_id: parseInt(form.irm_pharm_pharmacies_id),
    };
    try {
      setSaving(true);
      if (editing) {
        await api.pharmacy.updateLocation(editing.id, payload);
      } else {
        await api.pharmacy.createLocation(payload);
      }
      onSaved();
      onClose();
    } catch { } finally { setSaving(false); }
  };

  const pharmOptions = pharmacies.map((p) => ({ value: String(p.id), label: `${p.code} – ${p.name}` }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
        {editing ? 'Edit Location' : 'New Store Location'}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }} onKeyDown={formKeyHandler(handleSave)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SearchableSelect
              label="Pharmacy *"
              options={pharmOptions}
              value={form.irm_pharm_pharmacies_id}
              onChange={(v) => setForm((p) => ({ ...p, irm_pharm_pharmacies_id: v }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField autoFocus size="small" fullWidth label="Location Name *"
              value={form.name} onChange={up('name')}
              sx={{ '& .MuiOutlinedInput-root': { height: 36, fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField size="small" fullWidth label="Location Code"
              value={form.code} onChange={up('code')}
              sx={{ '& .MuiOutlinedInput-root': { height: 36, fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField size="small" fullWidth label="Address / Description"
              value={form.address} onChange={up('address')}
              sx={{ '& .MuiOutlinedInput-root': { height: 36, fontSize: '0.82rem' } }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Switch checked={form.is_default} size="small"
                onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.checked }))} />}
              label={<MDTypography variant="caption">Default</MDTypography>}
            />
          </Grid>
          {editing && (
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={form.is_active} size="small"
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />}
                label={<MDTypography variant="caption">Active</MDTypography>}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#005e6e' }, textTransform: 'none', fontWeight: 600, boxShadow: 'none', color: '#fff !important' }}
        >
          {saving ? 'Saving…' : `${editing ? 'Update' : 'Create'} (Ctrl+↵)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function LocationsScreen() {
  const { selectedPharmacy, pharmacies } = usePharmacy();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterPharmacy, setFilterPharmacy] = useState('');

  const kb = useListKeyboard({
    rowCount: rows.length,
    enabled: !dialogOpen,
    onOpen: (i) => { if (rows[i]) { setEditing(rows[i]); setDialogOpen(true); } },
    onCreate: () => { setEditing(null); setDialogOpen(true); },
  });

  useEffect(() => {
    if (selectedPharmacy && !filterPharmacy) {
      setFilterPharmacy(String(selectedPharmacy.id));
    }
  }, [selectedPharmacy, filterPharmacy]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterPharmacy ? { pharmacy_id: filterPharmacy } : {};
      const r = await api.pharmacy.listLocations(params);
      setRows(r.data || []);
    } catch { } finally { setLoading(false); }
  }, [filterPharmacy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: `Delete "${row.name}"?`, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete',
    });
    if (!isConfirmed) return;
    try { await api.pharmacy.updateLocation(row.id, { is_active: 'N', is_deleted: 'Y' }); fetchData(); } catch { }
  };

  const pharmOptions = [{ value: '', label: 'All Pharmacies' }, ...pharmacies.map((p) => ({ value: String(p.id), label: `${p.code} – ${p.name}` }))];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Banner */}
        <Box sx={{ mb: 2.5, p: 2.5, borderRadius: 2, background: `linear-gradient(135deg, ${TEAL} 0%, #004f5e 100%)`, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorefrontIcon sx={{ fontSize: 40, opacity: 0.85 }} />
          <Box>
            <MDTypography variant="h5" sx={{ color: '#fff !important', fontWeight: 800, lineHeight: 1.2 }}>Store Locations</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'rgba(255,255,255,0.7) !important', fontSize: '0.78rem' }}>
              Storage rooms, racks and sub-units within each pharmacy
            </MDTypography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 700, color: '#fff !important', backdropFilter: 'blur(4px)' }}
          >
            New Location (Alt+N)
          </Button>
        </Box>

        {/* Filter bar */}
        <Card sx={{ mb: 2, px: 2, py: 1.5 }}>
          <Box sx={{ maxWidth: 320 }}>
            <SearchableSelect
              label="Filter by Pharmacy"
              options={pharmOptions}
              value={filterPharmacy}
              onChange={(v) => setFilterPharmacy(v)}
            />
          </Box>
        </Card>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableRow>
                {['Code', 'Name', 'Pharmacy', 'Status', ''].map((h, i) => (
                  <TableCell key={i} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><CircularProgress size={24} sx={{ color: TEAL }} /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>No locations found</TableCell></TableRow>
                ) : rows.map((row, i) => {
                  const last = i === rows.length - 1;
                  return (
                    <TableRow key={row.id} hover {...kb.getRowProps(i)} sx={kb.activeIdx === i ? ACTIVE_ROW_SX : undefined}>
                      <TableCell sx={{ ...td(last), fontFamily: 'monospace', fontWeight: 700, color: TEAL, fontSize: '0.8rem' }}>
                        {row.code || '—'}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontWeight: 600 }}>{row.name}</Box>
                        {row.is_default === 'Y' && <Chip label="Default" size="small" color="info" sx={{ fontSize: '0.58rem', height: 16, mt: 0.3 }} />}
                        {row.address && <Box sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.2 }}>{row.address}</Box>}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontSize: '0.78rem' }}>{row.pharmacy?.name || '—'}</Box>
                        <Box sx={{ fontSize: '0.68rem', color: '#aaa', fontFamily: 'monospace' }}>{row.pharmacy?.code}</Box>
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Chip
                          label={row.is_active === 'Y' ? 'Active' : 'Inactive'} size="small"
                          color={row.is_active === 'Y' ? 'success' : 'default'}
                          sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditing(row); setDialogOpen(true); }}>
                              <EditIcon sx={{ fontSize: 15, color: '#888' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(row)}>
                              <DeleteIcon sx={{ fontSize: 15, color: '#d33' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </MDBox>

      <LocationFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchData}
        editing={editing}
        pharmacies={pharmacies}
      />
      <Footer />
    </DashboardLayout>
  );
}
